import { useEffect, useState } from "react";
import importMF from "../../federated/importMF";
import useUnmounted from "../useUnmounted";
import usePubSub from "../usePubSub";
import viafoura from "../../utils/viafoura/library";
import log from "../../utils/logger/library";
import { getFeedCookie } from "../../utils/cookies/library";
import { AUTHED_COOKIE, GUEST_COOKIE } from "../../utils/cookies/constants";
import {
    setLoginRadiusCookie,
    deleteLoginRadiusCookie,
} from "../../utils/membership";
import { isServer } from "../../utils/environmentDetection/library";
import { useSelector } from "react-redux";
import { ACC_MODULES } from "../../federated/accountCentre";

/**
 * Import the getSessionToken function from the account centre and execute it.
 * @returns {Promise<string|null>} A promise that resolves to a session token if successful, or null if there was an error.
 */
const getSessionToken = async () => {
    try {
        const getSessionTokenFromAccountCentre = await importMF(
            ACC_MODULES.getSessionToken
        );
        return await getSessionTokenFromAccountCentre();
    } catch (error) {
        log.error(
            `(useUserInfo) Failed to get session token from account centre [Error: ${error.message}]`
        );
        return null;
    }
};

export default function useUserInfo() {
    const isUnmounted = useUnmounted();
    const [isLoading, setLoading] = useState(true);
    const [isLoggedIn, setLoggedIn] = useState(false);
    const [id, setId] = useState("");
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const pubsub = usePubSub();

    const userProfileEmail = useSelector((state) => {
        if (state?.membership?.userProfile?.Email) {
            const fallbackEmailsList = state.membership.userProfile.Email;
            const fallbackEmail =
                fallbackEmailsList.find((email) => email.Type === "Primary") ||
                fallbackEmailsList[0];
            return fallbackEmail.Value;
        }
        return "";
    });

    // viafoura occasionally returns an empty string in the email field
    // in this case, we use the email from the membership store, if present
    const safelySetEmail = (viafouraEmail) => {
        setEmail(viafouraEmail || userProfileEmail);
    };

    useEffect(() => {
        if (isServer()) {
            return;
        }
        let onUser;
        let onLogin;
        let maybeUpdateUieId = () => {
            viafoura.getUserContext().then((user) => {
                let isLoggedIn = viafoura.isUserAuthenticated(user);
                const cookieName = isLoggedIn ? AUTHED_COOKIE : GUEST_COOKIE;
                const uieCookie = getFeedCookie(cookieName);
                // UIE cookoie will not yet exist during user + loaded
                // for a user signing in during the session session start
                // but it will after UIEAlert
                if (!uieCookie) {
                    return;
                }
                setId(uieCookie.value);
                setLoading(false);
            });
        };

        viafoura.addOnReadyCallback(() => {
            if (isUnmounted()) {
                return;
            }

            if (isServer()) {
                return;
            }

            const updateUser = (user) => {
                setLoggedIn(viafoura.isUserAuthenticated(user));
                setName(user.name);
                safelySetEmail(user.email);
                maybeUpdateUieId();
            };

            // on session start, cbc-stats* will have already initialized user
            viafoura.getUserContext().then(updateUser);

            onUser = window.vf.$subscribe("user", "loaded", (user) => {
                log.debug("user loaded", user.name);

                const isLoggedIn = viafoura.isUserAuthenticated(user);
                setLoggedIn(isLoggedIn);
                setName(user.name);
                safelySetEmail(user.email);
                maybeUpdateUieId();
            });

            onLogin = window.vf.$subscribe("login", "success", async (user) => {
                setLoading(true);
                log.debug("login success", user.name);
                setLoggedIn(viafoura.isUserAuthenticated(user));
                setName(user.name);
                safelySetEmail(user.email);
                setLoginRadiusCookie(await getSessionToken());
            });

            window.vf.$subscribe("logout", "success", () => {
                deleteLoginRadiusCookie();
            });

            pubsub.subscribe("UIEAlert", maybeUpdateUieId);
        });
        return () => {
            onUser && onUser.remove();
            onLogin && onLogin.remove();
            pubsub.unsubscribe("UIEAlert", maybeUpdateUieId);
        };
    }, []);

    return {
        id,
        name,
        email,
        isLoggedIn,
        isLoading,
    };
}
